/**
 * What the URL is telling the auth UI. Zero imports, per invariant 23.
 *
 * The API puts its outcome in a query parameter and redirects here, so this is
 * the seam between "the server did something" and "the person sees a sentence
 * about it". Pure, so the wording is unit-tested and cannot drift from the
 * server's actual outcomes.
 */

export type Banner = { kind: "ok" | "warn" | "danger"; text: string } | null;

/** The parameters the API can send back, and nothing else. */
export function bannerFor(search: string): Banner {
  const q = new URLSearchParams(search);
  switch (q.get("verified")) {
    case "1":
      return { kind: "ok", text: "Email confirmed. You are signed in." };
    case "expired":
      return {
        kind: "warn",
        text: "That confirmation link had already been used or has expired. Sign in and ask for a new one.",
      };
    case "taken":
      return {
        kind: "danger",
        text: "That address is already confirmed on another account. Sign in to that one, or reset its password.",
      };
  }
  switch (q.get("email")) {
    case "changed":
      return { kind: "ok", text: "Email address changed." };
    case "taken":
      return { kind: "danger", text: "That address is already confirmed on another account, so it was not changed." };
  }
  if (q.get("reset") === "expired") {
    return { kind: "warn", text: "That reset link had already been used or has expired. Ask for a new one." };
  }
  return null;
}

/**
 * The reset token the API handed to the app, if the URL carries one.
 *
 * Shape-checked here rather than trusted: it goes straight back to the server,
 * and a value that is not a token is not worth a round trip. Base64url, the
 * same alphabet randomToken produces.
 */
export function resetTokenFrom(search: string): string | null {
  const t = new URLSearchParams(search).get("reset");
  if (!t || t === "expired") return null;
  return /^[A-Za-z0-9_-]{16,512}$/.test(t) ? t : null;
}

/**
 * The URL with every auth parameter stripped, for history.replaceState.
 *
 * A reset token in the address bar is a credential in the address bar: it
 * reaches the referrer of anything the page later loads, browser history, and
 * whatever syncs that history between devices. It must survive exactly long
 * enough to be read once.
 */
export function urlWithoutAuthParams(href: string): string {
  const url = new URL(href);
  for (const key of ["reset", "verified", "email"]) url.searchParams.delete(key);
  return url.pathname + (url.searchParams.toString() ? `?${url.searchParams}` : "") + url.hash;
}
